export type CheckoutEvent =
  | "page_view"
  | "cart_viewed"
  | "shipping_form_submitted"
  | "payment_attempted"
  | "payment_failed"
  | "order_completed"
  | "checkout_step_completed";

export interface EventProperties {
  [key: string]: string | number | boolean | null | undefined;
}

class BrowserAnalytics {
  private sessionId: string | null = null;
  private isDevelopment: boolean;

  constructor() {
    // Check if PostHog is configured (would check for real key in production)
    this.isDevelopment =
      !import.meta.env.VITE_POSTHOG_API_KEY ||
      import.meta.env.VITE_POSTHOG_API_KEY.includes("placeholder");
  }

  init(sessionId: string): void {
    this.sessionId = sessionId;

    if (this.isDevelopment) {
      console.log("[Analytics] Initialized with session:", sessionId);
    }

    // In production, would initialize PostHog here:
    // posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
    //   api_host: import.meta.env.VITE_POSTHOG_HOST,
    //   person_profiles: 'identified_only',
    // });
    // posthog.identify(sessionId);
  }

  track(event: CheckoutEvent, properties?: EventProperties): void {
    if (!this.sessionId) {
      console.warn("[Analytics] Track called before init");
      return;
    }

    const eventData = {
      event,
      properties: {
        ...properties,
        session_id: this.sessionId,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
      },
    };

    if (this.isDevelopment) {
      console.log("[Analytics]", eventData);
    } else {
      // In production, would send to PostHog:
      // posthog.capture(event, eventData.properties);
    }
  }

  pageView(pageName: string, properties?: EventProperties): void {
    this.track("page_view", {
      page_name: pageName,
      ...properties,
    });
  }

  stepCompleted(step: string, properties?: EventProperties): void {
    this.track("checkout_step_completed", {
      step,
      ...properties,
    });
  }

  reset(): void {
    this.sessionId = null;

    if (this.isDevelopment) {
      console.log("[Analytics] Reset");
    } else {
      // In production:
      // posthog.reset();
    }
  }
}

export const analytics = new BrowserAnalytics();

export function trackCartViewed(itemCount: number, totalValue: number): void {
  analytics.track("cart_viewed", {
    item_count: itemCount,
    total_value: totalValue,
  });
}

export function trackShippingFormSubmitted(shippingMethod?: string): void {
  analytics.track("shipping_form_submitted", {
    shipping_method: shippingMethod,
  });
}

export function trackPaymentAttempted(amount: number, paymentMethod: string): void {
  analytics.track("payment_attempted", {
    amount,
    payment_method: paymentMethod,
  });
}

export function trackPaymentFailed(reason: string, errorCode?: string): void {
  analytics.track("payment_failed", {
    failure_reason: reason,
    error_code: errorCode,
  });
}

export function trackOrderCompleted(orderId: string, amount: number, itemCount: number): void {
  analytics.track("order_completed", {
    order_id: orderId,
    amount,
    item_count: itemCount,
  });
}

export function trackCheckoutStarted(itemCount: number, totalValue: number): void {
  analytics.track("checkout_step_completed", {
    step: "checkout_started",
    item_count: itemCount,
    total_value: totalValue,
  });
}

export function trackPurchaseCompleted(orderId: string, amount: number, itemCount: number): void {
  analytics.track("order_completed", {
    order_id: orderId,
    amount,
    item_count: itemCount,
    success: true,
  });
}
